import React from 'react';
import {
  categoryTrackLabel,
  categoryUrl,
  FaqCategory,
  HELP_LIST_TRACK_CAT,
  LinkWithHelpHighlight,
} from '../util';
import styled, { css } from 'styled-components';
import { Type, spacer16, spacer8 } from '@spotify-internal/encore-web';
import { SearchBar } from '../../Search';
import { useT } from '@spotify-internal/i18n-core';

const SearchBarWrapper = styled.div``;

// it seems the li needs to be styled, or else the ul inside will have a strange error
const SideMenuList = styled.ul`
  list-style: none;
  padding-left: 0;
`;

const SideMenuItem = styled.li`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;

  padding: ${spacer16} 0;

  border-bottom: 1px solid ${({ theme }) => theme.help.link.divider};
  &:last-child {
    border-bottom: none;
  }
`;

const linkColors = css<{ isCurrent: boolean }>`
  color: ${({ isCurrent, theme }) =>
    isCurrent
      ? theme.help.sideMenu.selectedCategory
      : theme.help.sideMenu.unselectedCategory};
`;

const SideMenuLink = styled(LinkWithHelpHighlight)`
  flex: 1;
  -webkit-tap-highlight-color: transparent;

  ${linkColors}
`;

const CategoryLabel = styled(Type).attrs({
  as: Type.span,
  variant: 'cello',
  weight: 'bold',
})``;

const CategoryCount = styled(Type).attrs({
  as: Type.span,
  variant: 'cello',
  weight: 'book',
})`
  flex: 0;
  ${linkColors}
`;

const CurrentCategoryIndicator = styled.div`
  flex: 0;
  min-width: ${spacer8};
  height: ${spacer8};
  margin-right: ${spacer8};
  border-radius: 50%;
  background-color: ${({ theme }) =>
    theme.help.sideMenu.currentCategoryIndicator};
`;

interface HelpListSideMenuType {
  currentCategory?: FaqCategory;
  categories: FaqCategory[];
}

export const HelpListSideMenu: React.FC<HelpListSideMenuType> = ({
  categories,
  currentCategory,
}) => {
  const t = useT();

  return (
    <>
      <SearchBarWrapper data-testid="help-search-bar">
        <SearchBar
          contentTypes={['faq']}
          placeholder={t('FRODOR_e6f5ed', 'Search help articles', '')}
          suggestCount={5}
          trackCategory={HELP_LIST_TRACK_CAT}
          getSuggestUrl={sug =>
            `${sug.url}?ref=sug_${HELP_LIST_TRACK_CAT}${
              currentCategory ? `&category=${currentCategory.urlSlug}` : ''
            }`
          }
        />
      </SearchBarWrapper>

      <SideMenuList>
        {categories.map((category: FaqCategory) => {
          const isCurrent = category.urlSlug === currentCategory?.urlSlug;
          return (
            <SideMenuItem key={category.urlSlug}>
              {isCurrent ? (
                <CurrentCategoryIndicator data-testid="current-category-indicator" />
              ) : null}
              <SideMenuLink
                track={[HELP_LIST_TRACK_CAT, categoryTrackLabel(category)]}
                href={categoryUrl(category)}
                isCurrent={isCurrent}
              >
                <CategoryLabel>{category.name}</CategoryLabel>
              </SideMenuLink>
              <CategoryCount isCurrent={isCurrent}>
                {category.faqs.length}
              </CategoryCount>
            </SideMenuItem>
          );
        })}
      </SideMenuList>
    </>
  );
};
